import Vue from 'vue';
import jsona from '@/lib/jsona';
import { generatePredefinedColor } from '@/utils/colorGenerationHelpers';
import { CHART_GROUPS } from '@/components/shared/visnet-hubs-common-consts';
import RootStateCommit from '@/store/mixins/rootstate-commit';
import VisnetHubEnvironmentalData from '@/resources/visnet-hub-environmental-data';

const AXIS_GROUPS = [
  {
    name: 'outdoorTemperature',
    mappedName: 'Outdoor Temperature',
    yAxis: 0,
    unit: '\u2103'
  },
  {
    name: 'indoorTemperature',
    mappedName: 'Indoor Temperature',
    yAxis: 0,
    unit: '\u2103'
  },
  {
    name: 'txTemperature',
    mappedName: 'Tx Temperature',
    yAxis: 0,
    unit: '\u2103'
  },
  {
    name: 'indoorHumidity',
    mappedName: 'Indoor Humidity',
    yAxis: 1,
    unit: '%'
  }
];

const assignToYAxis = serieName => AXIS_GROUPS.find(group => serieName === group.name);
const createChartSerie = (serieName, serieData) => {
  const { yAxis, unit, mappedName } = assignToYAxis(serieName);

  const environmentalColorParams = {
    chartGroupType: CHART_GROUPS.ENVIRONMENTAL,
    attributeName: serieName
  };
  const serieColor = generatePredefinedColor(environmentalColorParams);

  return {
    boostThreshold: 1,
    turboThreshold: 0,
    animation: false,
    color: serieColor,
    name: mappedName,
    data: serieData,
    yAxis,
    suffix: unit,
    marker: { enabled: true, radius: 3 },
    states: { hover: { lineWidthPlus: 0 } }
  };
};

const MODULE_NAME = 'environmentalData';
const DEFAULT_STATE = { data: {} };

export default {
  /* eslint no-param-reassign: ["error", { "props": false }] */
  namespaced: true,
  state: Object.assign(DEFAULT_STATE, {}),
  mutations: {
    parseResponseData(state, response) {
      const responseParsed = jsona.deserialize(response);

      Vue.set(state, 'data', responseParsed);
    },
    setError(state, error) {
      state.error = error;
    },
    resetEnvironmentalData(state) {
      state.data = {};
    },
    resetStore(state) {
      Object.assign(state, DEFAULT_STATE);
    }
  },
  actions: {
    async fetchEnvironmentalData({ commit }, visnetParams) {
      RootStateCommit(commit, 'startLoading', MODULE_NAME);

      commit('setError', null);

      const { externalVisnetHubId, projectCode, substationCode, startDate, endDate } = visnetParams;

      try {
        const response = await VisnetHubEnvironmentalData({ externalVisnetHubId }).get({
          project_code: projectCode,
          substation_code: substationCode,
          start_date: startDate,
          end_date: endDate
        });

        if (!response) throw Error;

        commit('parseResponseData', response.data);

        RootStateCommit(commit, 'stopLoading', MODULE_NAME);
        return response.ok;
      } catch (error) {
        commit('setError', error);

        RootStateCommit(commit, 'setResponseError', MODULE_NAME);
        RootStateCommit(commit, 'stopLoading', MODULE_NAME);

        return error.ok;
      }
    }
  },
  getters: {
    filteredEnvironmentalData: state => {
      const {
        endDate,
        externalVisnetHubId,
        id,
        projectCode,
        startDate,
        substationCode,
        type,
        ...environmentalAttributes
      } = state.data;

      const environmentalData = Object.keys(environmentalAttributes).map(
        attribute =>
          environmentalAttributes[attribute].length &&
          createChartSerie(attribute, environmentalAttributes[attribute])
      );

      return environmentalData.filter(series => series);
    }
  }
};
